import { ParameterGroup, ParameterOption } from '@/types/dataEditor';

/**
 * Parameter configuration for Data Editor.
 *
 * Parameters are grouped by TYPE (not source) for cleaner UX.
 * Each parameter can have multiple sources (devices that measure it).
 */

// Internal: All parameter definitions with their sources
const PARAMETER_DEFINITIONS: Array<{
  value: string;
  label: string;
  group: string;
  sources: Array<{ source: string; table: string }>;
}> = [
  // Apex Sensors
  {
    value: 'temp',
    label: 'Temperature (°C)',
    group: 'Apex Sensors',
    sources: [{ source: 'apex', table: 'measurements' }],
  },
  {
    value: 'ph',
    label: 'pH',
    group: 'Apex Sensors',
    sources: [
      { source: 'apex', table: 'measurements' },
      { source: 'alkatronic', table: 'measurements' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'salinity',
    label: 'Salinity',
    group: 'Apex Sensors',
    sources: [
      { source: 'apex', table: 'measurements' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },

  // Water Parameters
  {
    value: 'alkalinity',
    label: 'Alkalinity (dKH)',
    group: 'Water Parameters',
    sources: [
      { source: 'mastertronic', table: 'measurements' },
      { source: 'alkatronic', table: 'measurements' },
      { source: 'aquawiz', table: 'measurements' },
      { source: 'aquarimate', table: 'measurements' },
      { source: 'kamoer_kh', table: 'measurements' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'ca',
    label: 'Calcium (ppm)',
    group: 'Water Parameters',
    sources: [
      { source: 'mastertronic', table: 'water_test_results' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'mg',
    label: 'Magnesium (ppm)',
    group: 'Water Parameters',
    sources: [
      { source: 'mastertronic', table: 'water_test_results' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'no3',
    label: 'Nitrate (ppm)',
    group: 'Water Parameters',
    sources: [
      { source: 'mastertronic', table: 'water_test_results' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'po4',
    label: 'Phosphate (ppm)',
    group: 'Water Parameters',
    sources: [
      { source: 'mastertronic', table: 'water_test_results' },
      { source: 'aquarimate', table: 'water_test_results' },
      { source: 'manual', table: 'measurements' },
    ],
  },
  {
    value: 'k',
    label: 'Potassium (ppm)',
    group: 'Water Parameters',
    sources: [{ source: 'mastertronic', table: 'water_test_results' }],
  },
  {
    value: 'nh3',
    label: 'Ammonia (ppm)',
    group: 'Water Parameters',
    sources: [{ source: 'aquarimate', table: 'water_test_results' }],
  },

  // Kamoer-specific
  {
    value: 'ph_kamoer',
    label: 'pH (Kamoer)',
    group: 'Water Parameters',
    sources: [{ source: 'kamoer_kh', table: 'measurements' }],
  },

  // AquaWiz-specific
  {
    value: 'ph_aquawiz',
    label: 'pH (AquaWiz)',
    group: 'Water Parameters',
    sources: [{ source: 'aquawiz', table: 'water_test_results' }],
  },
  {
    value: 'ph_o',
    label: 'pH Output (AquaWiz)',
    group: 'Water Parameters',
    sources: [{ source: 'aquawiz', table: 'water_test_results' }],
  },
  {
    value: 'dosing_amount',
    label: 'Dosing Amount (mL)',
    group: 'Water Parameters',
    sources: [{ source: 'aquawiz', table: 'water_test_results' }],
  },
];

/**
 * Unique parameters grouped by type (for dropdown display).
 * Each parameter appears once, not duplicated per source.
 */
export const UNIQUE_PARAMETERS = PARAMETER_DEFINITIONS.map(p => ({
  value: p.value,
  label: p.label,
  group: p.group,
}));

/**
 * Core measured parameters for Data Editor dropdown.
 * Filtered to show only the main water quality parameters users want to edit.
 * Excludes device-specific variants and dosing-related parameters.
 */
export const DATA_EDITOR_PARAMETERS = [
  { value: 'temp', label: 'Temperature (°C)' },
  { value: 'ph', label: 'pH' },
  { value: 'salinity', label: 'Salinity' },
  { value: 'alkalinity', label: 'Alkalinity (dKH)' },
  { value: 'ca', label: 'Calcium (ppm)' },
  { value: 'mg', label: 'Magnesium (ppm)' },
  { value: 'no3', label: 'Nitrate (ppm)' },
  { value: 'po4', label: 'Phosphate (ppm)' },
];

/**
 * Parameter groups for Mantine Select display.
 */
export const PARAMETER_GROUPS: ParameterGroup[] = (() => {
  const groups: Record<string, ParameterOption[]> = {};

  PARAMETER_DEFINITIONS.forEach(param => {
    if (!groups[param.group]) {
      groups[param.group] = [];
    }
    // Add parameter with first source as default (for backward compat)
    const firstSource = param.sources[0];
    const group = groups[param.group];
    if (firstSource && group) {
      group.push({
        value: param.value,
        label: param.label,
        group: param.group,
        source: firstSource.source,
        table: firstSource.table,
      });
    }
  });

  return Object.entries(groups).map(([group, items]) => ({
    group,
    items,
  }));
})();

/**
 * Get all available sources for a parameter.
 * @param paramValue - Parameter key (e.g., "temp", "dkh")
 * @returns Array of source names, empty array if parameter not found
 */
export function getSourcesForParameter(paramValue: string): string[] {
  const param = PARAMETER_DEFINITIONS.find(p => p.value === paramValue);
  if (!param) return [];
  return param.sources.map(s => s.source);
}

/**
 * Get clean label for a parameter (without source suffix).
 * @param paramValue - Parameter key (e.g., "temp", "dkh")
 * @returns User-friendly label, or the parameter key if not found
 */
export function getParameterLabel(paramValue: string): string {
  const param = PARAMETER_DEFINITIONS.find(p => p.value === paramValue);
  if (!param) return paramValue;
  return param.label;
}

/**
 * Check if a parameter has multiple sources.
 */
export function hasMultipleSources(paramValue: string): boolean {
  return getSourcesForParameter(paramValue).length > 1;
}

// Flatten for easy lookup (backward compatibility)
export const ALL_PARAMETERS = PARAMETER_GROUPS.flatMap(g => g.items);

// Helper to format value by parameter type
export function formatValue(value: number, parameter: string): string {
  if (parameter === 'po4') {
    return value.toFixed(3);  // 0.035
  } else if (['ca', 'mg'].includes(parameter)) {
    return Math.round(value).toString();  // 420, 1350
  } else {
    return value.toFixed(2);  // 25.34, 8.21
  }
}

/**
 * Human-readable source labels for display.
 */
export const SOURCE_LABELS: Record<string, string> = {
  apex: 'Apex',
  apex_fusion: 'Apex',
  mastertronic: 'Mastertronic',
  aquawiz: 'AquaWiz',
  aquarimate: 'Aquarimate',
  alkatronic: 'Alkatronic',
  focustronic: 'Focustronic',
  kamoer_kh: 'Kamoer KH',
  kamoer_stp: 'Kamoer STP',
  kamoer_p4: 'Kamoer P4',
  manual: 'Manual',
};
